<template>
  <div class="p-fluid p-formgrid p-grid">
    <div class="p-col-12">
      <div class="center-container">
        <h1 class="title is-1" v-if="existingVenue">{{ $t("edit-venue") }}</h1>
        <h1 class="title is-1" v-else>{{ $t("AppMenu.new_venue") }}</h1>
        <form autocomplete="off">
          <div class="p-grid p-mt-1">
            <div v-for="(venue, i) in venues" :key="i" class="p-grid p-col-12 mb-3">
              <div :class="`p-col-${existingVenue ? 6 : 5}`">
                <b-field :label="$t('location-name')" class="p-d-block">
                  <vue-google-autocomplete
                    :id="`map${i}`"
                    :ref="`address${i}`"
                    classname="input"
                    types="establishment"
                    :value="venue.name"
                    :placeholder="$t('Form.shared.start_typing')"
                    v-on:placechanged="
                      (addressData, placeResultData) => getAddressData(venue, addressData, placeResultData)
                    "
                    @keyup="name => locationNameChange(venue, name)"
                  />
                  <a @click="addVenueManually(venue)" style="color: #1386e1; font-size: 0.8em;">
                    {{ $t('add-venue-manually') }}
                  </a>
                </b-field>
              </div>

              <div class="p-col-2">
                <b-field :label="$t('venue-playing-surfaces')" class="p-d-block">
                  <div v-for="(surface, j) in venue.surfaces" :key="j" class="mb-1">
                    <b-input v-model="surface.name" @input="surfaceInputChange(venue)" :placeholder="$t('surface')"></b-input>
                    <a
                      @click="startDeleteSurface(venue, surface, j)"
                      style="color: red; font-size: 0.8em;"
                      v-if="venue.surfaces.length > 1"
                    >
                      {{ $t("Button.delete") }}
                    </a>
                    <a
                      @click="addSurface(venue)"
                      class="p-d-block mt-1"
                      style="color: #1386e1; font-size: 0.8em;"
                      v-if="j === venue.surfaces.length - 1"
                    >
                      {{ $t('add-surface') }}
                    </a>
                  </div>
                </b-field>
              </div>

              <div class="p-col-2">
                <b-field :label="$t('add-home-teams')">
                  <Button @click="addHomeTeams(venue)" class="p-button-secondary p-button-sm button-padding fit">
                    {{ $t('add-home-teams') }}
                  </Button>
                </b-field>

                <div class="column comma-list">
                  <span v-for="venue_team in venue.home_teams" :key="venue_team.id">
                    <a>{{ venue_team.name }}</a>
                  </span>
                </div>
              </div>

              <div class="p-col-2">
                <div class="field-without-label">
                  <Button
                    @click="editVenueSettings(venue)"
                    class="p-button-outlined p-button-sm rounded button-padding fit"
                  >
                  {{ $t('edit-venue-settings') }}
                  </Button>
                </div>
              </div>

              <div class="p-col-1" v-if="!existingVenue">
                <div class="field-without-label">
                  <Button
                    class="p-button-link danger p-button-sm button-padding fit danger-color"
                    :disabled="i === 0"
                    @click="deleteVenueRow(i)"
                  >
                    {{ $t('delete') }}
                  </Button>
                </div>
              </div>

              <hr style="width: 100%; background-color: #dfdfdf; margin-top: 44px" v-if="i !== venues.length - 1" />
            </div>

            <div class="p-col-12 p-my-5">
              <div style="margin: 0 auto">
                <a
                  @click="createAnotherVenue"
                  style="display: block; color: green; font-size: 1em; margin-bottom: 15px;"
                  v-if="!existingVenue"
                >
                  {{ $t('create-another-venue') }}
                </a>
                <Button class="p-button-lg" @click="saveVenue" style="width: fit-content;" :disabled="!isValid">
                  {{ existingVenue ? $t("Button.save_venue") : $t("Button.save_venues") }}
                </Button>
              </div>
            </div>
          </div>

          <div v-if="!existingVenue" style="width: fit-content; margin: auto;">
            <PapaParse @save="doBulk" item="venue"></PapaParse>
            <a href="/csvs/venues.csv" target="_blank"> {{ $t("CSV.download_sample_file") }}</a>
          </div>

          <v-popover>
            <Button v-if="existingVenue" :label="$t('delete-venue')" class="p-button-text p-button-danger" />
            <template slot="popover">
              <div class="popover-wrapper">
                <div class="popover-title">{{ $t("confirm-delete-venue") }}</div>
                <div class="popover-text">
                  {{ $t("venue-and-associated-games-and-timeslots-will-be-deleted-this-cannot-be-undone") }}
                </div>
                <div class="popover-actions">
                  <Button
                    class="p-button p-button-danger p-button-text"
                    :label="$t('delete-venue')"
                    @click="deleteVenue"
                  />
                  <Button v-close-popover class="p-button p-button-text" :label="$t('Button.cancel')" />
                </div>
              </div>
            </template>
          </v-popover>
        </form>
      </div>
    </div>

    <Dialog
      :visible.sync="addVenueManuallyModal"
      :modal="true"
      :style="{ minWidth: '500px', maxWidth: '800px' }"
      :closable="false"
      v-if="currentVenue"
    >
      <template #header>
        <h2 class="title is-4 w100 has-text-centered">
          {{ $t('add-venue-manually') }}
          <font-awesome-icon class="close-modal-icon" :icon="['fal', 'times-circle']" @click="closeVenueMannually" />
        </h2>
      </template>

      <div class="p-grid normal-venue-labels">
        <div class="p-col-12">
          <b-field :label="$t('venue-name-required')">
            <b-input :autofocus="true" v-model.trim="currentVenue.name" :placeholder="$t('venue-name')" />
          </b-field>
        </div>

        <div class="p-col-12 label mt-6 mb-0">
          {{ $t('venue-details-optional') }}
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.address_line_1')">
            <b-input v-model.trim="currentVenue.address.line_1" :placeholder="$t('line')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('address-line-2')">
            <b-input v-model.trim="currentVenue.address.line_2" :placeholder="$t('line-2')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.city')">
            <b-input v-model.trim="currentVenue.address.city" :placeholder="$t('venue-city')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.state')">
            <b-input v-model.trim="currentVenue.address.state" :placeholder="$t('venue-state')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.country')">
            <b-input v-model.trim="currentVenue.address.country" placeholder="USA" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.zip')">
            <b-input v-model.trim="currentVenue.address.zip" :placeholder="$t('venue-zip')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.latitude')">
            <b-input v-model.trim="currentVenue.address.latitude" :placeholder="$t('lat')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('Wizard.manual_venue_form.longitude')">
            <b-input v-model.trim="currentVenue.address.longitude" :placeholder="$t('lon')" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('primary-phone')">
            <b-input v-model.trim="currentVenue.address.phone_primary" placeholder="3215551160" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('PersonFrom.primary-email')">
            <b-input v-model.trim="currentVenue.address.email_primary" placeholder="example@gmail.com" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('google-maps-link')">
            <b-input
              v-model.trim="currentVenue.address.link"
              placeholder="https://maps.google.com/?cid=7184227826847941611"
            />
          </b-field>
        </div>
      </div>

      <div class="p-m-auto mt-5" style="width: fit-content">
        <Button @click="closeVenueMannually" class="p-button-danger">
          {{ $t('save-venue') }}
        </Button>

        <a @click="closeVenueMannually" style="display: block; color: red; font-size: 0.8em; margin-top: 15px;">
          {{ $t('cancel') }}
        </a>
      </div>
    </Dialog>

    <Dialog
      :visible.sync="editVenueSettingsModal"
      :modal="true"
      :style="{ minWidth: '500px', maxWidth: '800px' }"
      :closable="false"
      v-if="currentVenue"
    >
      <template #header>
        <h2 class="title is-4 w100 has-text-centered">
          {{ $t('edit-venue-settings') }}
          <font-awesome-icon class="close-modal-icon" :icon="['fal', 'times-circle']" @click="closeVenueSettings" />
        </h2>
        <br />
      </template>

      <div class="p-grid">
        <div class="p-col-6">
          <b-field :label="$t('venue-priority-optional')">
            <InputNumber
              size="is-small"
              v-model="currentVenue.priority"
              showButtons
              buttonLayout="horizontal"
              spinnerMode="horizontal"
              :step="1"
              :min="-3"
              :max="3"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
            />
          </b-field>
          <small class="has-text-grey">{{ $t("3-is-lowest-0-is-neutral-3-is-highest") }}</small>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('force-venue-neutrality')">
            <b-checkbox v-model="currentVenue.force_neutral">
              {{ $t("let-all-teams-use-this-venue-as-home-teams-even-if-explicit-home-teams-are-assigned") }}
            </b-checkbox>
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('primary-phone')">
            <b-input v-model.trim="currentVenue.address.phone_primary" placeholder="3215551160" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('secondary-phone')">
            <b-input v-model.trim="currentVenue.address.phone_secondary" placeholder="3215551160" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('PersonFrom.primary-email')">
            <b-input v-model.trim="currentVenue.address.email_primary" placeholder="example@gmail.com" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('secondary-email')">
            <b-input v-model.trim="currentVenue.address.email_secondary" placeholder="example@gmail.com" />
          </b-field>
        </div>

        <div class="p-col-6">
          <b-field :label="$t('venue-notes')">
            <b-input
              v-model.trim="currentVenue.notes"
              :placeholder="$t('optional-venue-notes-instructions-to-find-park-etc')"
              type="textarea"
              rows="3"
            >
            </b-input>
          </b-field>
        </div>
      </div>

      <div class="p-m-auto mt-5" style="width: fit-content">
        <Button @click="closeVenueSettings" class="p-button-danger">
          {{ $t('save-venue') }}
        </Button>
      </div>
    </Dialog>

    <Dialog
      :visible.sync="addHomeTeamsModal"
      :modal="true"
      :style="{ minWidth: '500px', maxWidth: '800px' }"
      :closable="false"
      v-if="currentVenue"
    >
      <template #header>
        <h2 class="title is-4 w100 has-text-centered">
          {{ $t("edit-home-teams") }}
          <font-awesome-icon class="close-modal-icon" :icon="['fal', 'times-circle']" @click="closeHomeTeams" />
        </h2>
        <br />
      </template>

      <p class="mb-5">{{ $t("click-a-team-to-add-or-remove-from-list") }}</p>

      <div class="columns">
        <div class="column">
          <p class="bold">{{ $t("eligible-teams") }}</p>
          <div v-if="eligibleTeams.length > 0">
            <div v-for="team in eligibleTeams" :key="team.id">
              <a @click="addHomeTeamToVenue(team)">
                {{ team.name }}
              </a>
            </div>
          </div>
        </div>
        <div class="column">
          <p class="bold">{{ $t("home-teams") }}</p>
          <div v-for="venue_team in currentVenue.home_teams" :key="venue_team.id">
            <a @click="removeHomeTeamFromVenue(venue_team)">
              {{ venue_team.name }}
            </a>
          </div>
        </div>
      </div>

      <div class="p-m-auto mt-5" style="width: fit-content">
        <Button @click="closeHomeTeams" class="p-button-danger">
          {{ $t('done') }}
        </Button>
      </div>
    </Dialog>
  </div>
</template>

<script>
import api from "@/api";
import VueGoogleAutocomplete from "vue-google-autocomplete";

export default {
  components: { VueGoogleAutocomplete },
  data() {
    return {
      existingVenue: null,
      currentVenue: null,
      addVenueManuallyModal: false,
      addHomeTeamsModal: false,
      editVenueSettingsModal: false,
      venues: [
        {
          name: "",
          notes: "",
          priority: 0,
          is_neutral: true,
          force_neutral: false,
          surfaces: [
            {
              name: this.$t("GameTable.column.surface") + "1",
              notes: "",
              priority: 1,
            },
          ],
          gaddress: {},
          season_id: this.$store.getters.currentSeason.id,
          color: undefined,
          address: {
            formatted: "",
            line_1: "",
            line_2: "",
            city: "",
            state: "",
            zip: "",
            latitude: "",
            longitude: "",
            link: "",
            phone_primary: "",
            phone_secondary: "",
            email_primary: "",
            email_secondary: "",
          },
          home_teams: [],
        },
      ],
    };
  },
  methods: {
    startDeleteSurface(venue, surface, index) {
      let x = window.confirm(
        this.$t(
          "are-you-sure-you-want-to-delete-this-playing-surface-this-will-delete-all-timeslots-games-and-practices-here-to-preserve-these-press-cancel-and-then-move-them-to-another-surface-before-deleting",
        ),
      );
      if (x) {
        venue.surfaces.splice(index, 1);
      }
    },
    updateColor(color) {
      this.venue.color = color.hex;
    },
    deleteVenue() {
      api
        .deleteVenue(this.existingVenue.id)
        .then(() => {
          this.makeToast("info", this.$t("deleted-venue"));
          this.$store.dispatch("getLeagueInformation");
          this.goRouter(`/`);
        })
        .catch(error => {
          this.errors = error.response.data.errors;
          alert(this.errors);
        });
    },
    locationNameChange: function(venue, event) {
      venue.name = event.target.value;
    },
    getAddressData: function(venue, addressData, placeResultData) {
      venue.gaddress = addressData;
      venue.address.formatted = placeResultData.formatted_address;
      venue.address.line_1 = ((venue.gaddress.street_number || "") + " " + venue.gaddress.route).trim();
      venue.address.city = venue.gaddress.locality;
      venue.address.state = venue.gaddress.administrative_area_level_1;
      venue.address.zip = venue.gaddress.postal_code;
      venue.address.country = venue.gaddress.country;
      venue.address.latitude = venue.gaddress.latitude;
      venue.address.longitude = venue.gaddress.longitude;
      venue.address.phone_primary = placeResultData.formatted_phone_number;
      venue.name = placeResultData.name;
      venue.address.link = placeResultData.url;
    },
    saveVenue() {
      if (this.existingVenue && this.existingVenue.id) {
        api
          .updateVenue(this.existingVenue)
          .then(response => {
            this.makeToast("success", this.$t("saved-venue"));
            this.$store.dispatch("getLeagueInformation");
            this.proceed(`/venues/` + response.data.id);
          })
          .catch(error => {
            console.error(error);
          });
        return;
      }
      api
        .createVenues(this.venues)
        .then(() => {
          this.makeToast("success", this.$t("created-venue"));
          this.$store.dispatch("getLeagueInformation");
          setTimeout(() => this.proceed("/"), 700);
        })
        .catch(error => {
          console.error(error);
        });
    },
    addVenueManually(venue) {
      this.currentVenue = venue;
      this.addVenueManuallyModal = true;
    },
    addHomeTeams(venue) {
      this.currentVenue = venue;
      this.addHomeTeamsModal = true;
    },
    editVenueSettings(venue) {
      this.currentVenue = venue;
      this.editVenueSettingsModal = true;
    },
    closeVenueMannually() {
      this.currentVenue = null;
      this.addVenueManuallyModal = false;
    },
    closeHomeTeams() {
      this.currentVenue = null;
      this.addHomeTeamsModal = false;
    },
    closeVenueSettings() {
      this.currentVenue = null;
      this.editVenueSettingsModal = false;
    },
    addHomeTeamToVenue(team) {
      this.currentVenue.home_teams.push(team);
    },
    removeHomeTeamFromVenue(team) {
      this.currentVenue.home_teams = this.currentVenue.home_teams.filter(t => t.id !== team.id);
    },
    deleteVenueRow(index) {
      this.venues = this.venues.filter((_, i) => i !== index);
    },
    surfaceInputChange(venue) {
      const lastSurface = venue.surfaces[venue.surfaces.length - 1];

      if (lastSurface && lastSurface.name) {
        venue.surfaces.push({
          name: "",
          notes: "",
          priority: 1,
        });
      }
    },
    addSurface(venue) {
      venue.surfaces.push({
        name: "",
        notes: "",
        priority: 1,
      });
    },
    createAnotherVenue() {
      this.venues.push({
        name: "",
        notes: "",
        priority: 0,
        is_neutral: true,
        surfaces: [
          {
            name: this.$t("GameTable.column.surface") + "1",
            notes: "",
            priority: 1,
          },
        ],
        gaddress: {},
        season_id: this.$store.getters.currentSeason.id,
        color: undefined,
        address: {
          formatted: "",
          line_1: "",
          line_2: "",
          city: "",
          state: "",
          zip: "",
          latitude: "",
          longitude: "",
          link: "",
          phone_primary: "",
          phone_secondary: "",
          email_primary: "",
          email_secondary: "",
        },
        home_teams: [],
      });
    },
    doBulk(links) {
      api
        .createBulkVenues(this.$store.getters.currentSeason.id, links)
        .then(() => {
          this.makeToast("success", this.$t("Toast.venue_created"));
          this.$store.dispatch("getLeagueInformation");
          this.goRouter("/");
        })
        .catch(() => {
          alert(this.errors);
        });
    },
  },
  computed: {
    isValid() {
      return this.venues.every(v => v.surfaces.some(s => s.name));
    },
    venueTeamIDs() {
      const team_ids = [];
      if (this.currentVenue) {
        this.currentVenue.home_teams.forEach(team => {
          team_ids.push(team.id);
        });
      }
      return team_ids;
    },
    eligibleTeams() {
      return this.$store.getters.currentSeason.teams.filter(el => {
        return this.venueTeamIDs.indexOf(el.id) < 0;
      });
    },
  },
  created() {
    if (this.$route.params.venue_id) {
      api.getVenue(this.$route.params.venue_id, this.$store.getters.currentSeason.id).then(response => {
        const venue = response.data;
        venue.home_teams = venue.home_teams.map(vt => vt.team);
        this.venues = [venue];
        this.currentVenue = venue;
        this.existingVenue = venue;
      });
    }
  },
  mounted() {
    this.venues = [
      {
        name: "",
        notes: "",
        priority: 0,
        is_neutral: true,
        surfaces: [
          {
            name: this.$t("GameTable.column.surface") + "1",
            notes: "",
            priority: 1,
          },
        ],
        gaddress: {},
        season_id: this.$store.getters.currentSeason.id,
        color: undefined,
        address: {
          formatted: "",
          line_1: "",
          line_2: "",
          city: "",
          state: "",
          zip: "",
          latitude: "",
          longitude: "",
          link: "",
          phone_primary: "",
          phone_secondary: "",
          email_primary: "",
          email_secondary: "",
        },
        home_teams: [],
      },
    ];
    this.currentVenue = null;
    this.existingVenue = null;
    this.$store.dispatch("getLeagueInformation");
    this.$refs.address0[0].focus();
    window.analytics.page();
  },
};
</script>

<style scoped lang="scss">
form {
  margin-top: 1rem;
}

.field {
  margin-top: 20px;

  .label {
    margin-bottom: 5px;
  }
}

.field-without-label {
  margin-top: 3.26rem;
}

.button-padding {
  padding: 10px !important;
}

.fit {
  width: fit-content !important;
}

.danger-color {
  color: #d32f2f !important;
}
</style>
